import React, { useState } from 'react';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

import Footer from '../../components/footer/index';
import Section1 from '../../components/layanan/traffic_management/section';
import { 
  Activity, 
  BarChart3, 
  Shield, 
  Zap, 
  Settings, 
  Users, 
  CheckCircle, 
  MessageCircle,
  ArrowRight,
  Clock,
  Gauge,
  Network,
  TrendingUp,
  Server,
  Globe,
  AlertCircle,
  Target,
  Eye,
  Filter
} from 'lucide-react';

const TrafficManagementPage = () => {
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('qos');

  const trafficFeatures = [
    {
      icon: <Gauge className="w-8 h-8" />,
      title: "Quality of Service (QoS)",
      description: "Prioritaskan traffic penting dengan mengatur bandwidth berdasarkan aplikasi dan pengguna",
      color: "bg-blue-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Real-time Monitoring",
      description: "Monitor penggunaan bandwidth secara real-time dengan dashboard analitik yang komprehensif",
      color: "bg-purple-500"
    },
    {
      icon: <Filter className="w-8 h-8" />,
      title: "Traffic Shaping",
      description: "Mengontrol kecepatan dan volume data untuk memastikan distribusi bandwidth yang optimal",
      color: "bg-green-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "DDoS Protection",
      description: "Perlindungan otomatis dari serangan DDoS dengan sistem deteksi dan mitigasi yang canggih",
      color: "bg-red-500"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Application Control",
      description: "Kontrol akses aplikasi dengan whitelist/blacklist dan pengaturan bandwidth per aplikasi",
      color: "bg-yellow-500"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Load Balancing",
      description: "Distribusi traffic otomatis ke multiple server untuk performa dan availability optimal",
      color: "bg-indigo-500"
    }
  ];

  const useCases = {
    qos: [
      {
        title: "Prioritas Traffic Bisnis",
        description: "Berikan prioritas tinggi untuk aplikasi bisnis kritis seperti video conference dan ERP system",
        image: "/image/business_priority.png",
        benefits: ["Video conference lancar", "ERP system responsif", "Email priority tinggi"]
      },
      {
        title: "Bandwidth Allocation",
        description: "Alokasi bandwidth yang adil berdasarkan departemen, user group, atau jenis aplikasi",
        image: "/image/bandwidth_allocation.png",
        benefits: ["Alokasi per departemen", "Fair usage policy", "Dynamic allocation"]
      }
    ],
    monitoring: [
      {
        title: "Network Analytics",
        description: "Dashboard komprehensif untuk monitoring traffic pattern dan network performance",
        image: "/image/network_analytics.png",
        benefits: ["Real-time dashboard", "Historical reports", "Trend analysis"]
      },
      {
        title: "Alert System",
        description: "Sistem notifikasi otomatis untuk anomali traffic dan threshold bandwidth",
        image: "/image/alert_system.png",
        benefits: ["Real-time alerts", "Custom thresholds", "Multi-channel notifications"]
      }
    ],
    security: [
      {
        title: "Threat Detection",
        description: "Deteksi dini untuk aktivitas mencurigakan dan upaya serangan jaringan",
        image: "/image/threat_detection.png",
        benefits: ["AI-based detection", "Automated response", "Forensic analysis"]
      },
      {
        title: "Content Filtering",
        description: "Filter konten berbahaya dan tidak produktif untuk meningkatkan keamanan dan produktivitas",
        image: "/image/content_filtering.png",
        benefits: ["URL filtering", "Malware protection", "Category blocking"]
      }
    ]
  };

  const trafficPackages = [
    {
      id: 1,
      name: "Traffic Management Basic",
      popular: false,
      color: "from-blue-500 to-blue-700",
      bandwidth: "Up to 100 Mbps",
      users: "50 Users",
      monitoring: "Basic Dashboard",
      features: [
        "Quality of Service (QoS)",
        "Basic Traffic Shaping",
        "Real-time Monitoring",
        "Bandwidth Allocation",
        "Email Support",
        "Monthly Reports"
      ]
    },
    {
      id: 2,
      name: "Traffic Management Professional",
      popular: true,
      color: "from-green-500 to-green-700",
      bandwidth: "Up to 1 Gbps",
      users: "200 Users",
      monitoring: "Advanced Analytics",
      features: [
        "All Basic Features",
        "Advanced Traffic Shaping",
        "Application Control",
        "DDoS Protection",
        "Load Balancing",
        "Priority Support",
        "Custom Policies",
        "API Integration"
      ]
    },
    {
      id: 3,
      name: "Traffic Management Enterprise",
      popular: false,
      color: "from-purple-500 to-purple-700",
      bandwidth: "Unlimited",
      users: "Unlimited",
      monitoring: "Enterprise Dashboard",
      features: [
        "All Professional Features",
        "Multi-site Management",
        "Advanced Security",
        "Custom Integration",
        "Dedicated Support",
        "SLA Guarantee 99.9%",
        "On-site Installation",
        "24/7 Monitoring"
      ]
    }
  ];

  const performanceMetrics = [
    { metric: "Latency Reduction", value: "75%", description: "Average latency improvement" },
    { metric: "Bandwidth Efficiency", value: "90%", description: "Optimal bandwidth utilization" },
    { metric: "Uptime Guarantee", value: "99.9%", description: "Service availability" },
    { metric: "Threat Detection", value: "99%", description: "Security threat detection rate" }
  ];

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (packageName = "Traffic Management") => {
    const phoneNumber = "6287778882022";
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut mengenai harga dan implementasinya?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'
    }`}>
      
      
      
      {/* Hero Section */}
      <Section1 className="py-20 px-4 relative overflow-hidden" />

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Fitur Traffic Management
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Kontrol penuh atas lalu lintas jaringan dengan teknologi canggih
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {trafficFeatures.map((feature, index) => (
              <div
                key={index}
                className={`group p-6 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 hover:shadow-2xl transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'
                }`}
              >
                <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4 ${
                  feature.color || 'bg-[#1E9B4C]'
                }`}>
                  {feature.icon}
                </div>

                <h3 className={`text-xl font-bold mb-3 transition-colors ${
                  isDarkMode
                    ? 'text-white group-hover:text-[#1E9B4C]'
                    : 'text-gray-900 group-hover:text-[#1E9B4C]'
                }`}>
                  {feature.title}
                </h3>

                <p className={`transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 group-hover:text-gray-200' 
                    : 'text-gray-600 group-hover:text-gray-700'
                }`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Performance Metrics */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Hasil Performa
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Hasil nyata yang dicapai dengan traffic management kami
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {performanceMetrics.map((metric, index) => (
              <div
                key={index}
                className={`text-center p-6 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-200 shadow-gray-300/20'
                }`}
              >
                <div className="text-4xl font-bold text-[#1E9B4C] mb-2">
                  {metric.value}
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {metric.metric}
                </h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {metric.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Solusi Berdasarkan Kebutuhan
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Temukan solusi yang tepat untuk kebutuhan spesifik Anda
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className={`inline-flex rounded-lg p-1 shadow-lg ${
              isDarkMode 
                ? 'bg-gray-800 border border-gray-700' 
                : 'bg-white border border-gray-200'
            }`}>
              {Object.keys(useCases).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'qos'
                    ? 'Quality'
                    : tab === 'monitoring'
                    ? 'Monitoring'
                    : 'Security'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases[activeTab].map((useCase, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {useCase.title}
                  </h3>
                  <p className={`mb-4 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {useCase.description}
                  </p>
                  <div className="space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Paket Traffic Management
            </h2>
            <p className={`text-lg ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Pilih paket yang sesuai dengan kebutuhan jaringan perusahaan Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trafficPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative rounded-2xl p-8 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  pkg.popular
                    ? `bg-gradient-to-br ${pkg.color} text-white shadow-2xl transform scale-105`
                    : isDarkMode
                    ? 'bg-gray-800 text-white border border-gray-700 shadow-gray-900/50'
                    : 'bg-white text-gray-900 border border-gray-200 shadow-gray-300/20'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <div className="mb-4">
                    <span className="text-lg font-medium">Konsultasi untuk penawaran terbaik</span>
                  </div>
                </div>

                <div className="mb-8">
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Bandwidth</span>
                    <span className="font-bold">{pkg.bandwidth}</span>
                  </div>
                  <div className={`flex justify-between mb-2 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Users</span>
                    <span className="font-bold">{pkg.users}</span>
                  </div>
                  <div className={`flex justify-between mb-4 pb-2 border-b ${
                    pkg.popular 
                      ? 'border-white/20' 
                      : isDarkMode 
                      ? 'border-gray-700' 
                      : 'border-gray-200'
                  }`}>
                    <span>Monitoring</span>
                    <span className="font-bold">{pkg.monitoring}</span>
                  </div>

                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.popular ? 'text-white' : 'text-[#1E9B4C]'
                        }`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => contactViaWhatsApp(pkg.name)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-105 ${
                    pkg.popular 
                      ? 'bg-white text-green-700 hover:bg-gray-100 shadow-lg' 
                      : 'bg-[#1E9B4C] text-white hover:bg-green-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <MessageCircle className="w-5 h-5" />
                  Hubungi Kami
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
      }`}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Optimalkan Performa Jaringan Anda Sekarang
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Dapatkan konsultasi gratis dari tim expert kami untuk menentukan solusi traffic management terbaik
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("Traffic Management Consultation")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("Traffic Management Enterprise")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Hubungi Sales
            </button>
          </div>
        </div>
      </section>
        
      <Footer />
    </div>
  );
};

export default TrafficManagementPage;